/**
 * Home template
 * This file contains logic specific to a template.
 * It is intended to ONLY be loaded on templates that require
 * this specific functionality.
 **/

import Global from './lib/global';
import KrempRules from './lib/partials/krempRules';
import Logger from './lib/logger';
import ActionArea from './lib/partials/ActionArea';
import ZipInCal from './lib/partials/ZipInCal';
import ShippingRates from './lib/partials/ShippingRates';
import './lib/partials/ReserveButtonChecker';
import './lib/partials/OrderConflictChecker';
import './lib/partials/OrderConflictModal';
import './lib/partials/CartCheckoutEnabler';
// Vars
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

const NAME = 'Home';

// DOM elements
const actionAreaDoms = document.querySelectorAll('[data-action-area]');

const collectionReadMoreDoms = document.querySelectorAll('[data-collection-banner-read-more]');

const plpZipCodeDoms = document.querySelectorAll('[data-plp-zip-code]');

const zipNCalDoms = document.querySelectorAll('[data-zip-n-cal]');

// Logic
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

/**
 * init - Initialize the page
 **/
const init = () => {
	Logger.info(`${NAME} > loaded`);
	initActionAreas();
	initCollectionReadMore();
	initPLPZipCode();
	initZipNCal();
}

/**
 * initActionAreas - Initialize the action areas
 */
const initActionAreas = () => {
	Logger.info(`${NAME} > initActionAreas`);

	if (!actionAreaDoms.length) return;

	actionAreaDoms.forEach((actionAreaDom) => {
		const actionArea = new ActionArea(actionAreaDom);
		actionArea.init();
	});
}

/**
 * initCollectionReadMore - Initialize the collection read more functionality
 */
const initCollectionReadMore = () => {
	Logger.info(`${NAME} > initCollectionReadMore`);

	if (!collectionReadMoreDoms.length) return;

	collectionReadMoreDoms.forEach((collectionReadMoreDom) => {
		collectionReadMoreDom.addEventListener('click', (e) => {
			e.preventDefault();
			collectionReadMoreDom.parentNode.classList.toggle('collection-banner__description-wrap--active');

			if (collectionReadMoreDom.parentNode.classList.contains('collection-banner__description-wrap--active')) {
				collectionReadMoreDom.innerHTML = 'Read Less';
			} else {
				collectionReadMoreDom.innerHTML = 'Read More';
			}
		});
	});
}

/**
 * initZipNCal
 */
const initZipNCal = () => {

	Logger.info(`${NAME} > initZipNCal`);

	if (!zipNCalDoms.length) return;

	zipNCalDoms.forEach((zipNCalDom) => {
		const el = new ZipInCal(zipNCalDom);
		el.init();
		new ShippingRates(el.flatpickrInput, el.rulesPayload.rules);
	});
}

/**
 * initPLPZipCode - Initialize the PLP Zip Code functionality

 */
const initPLPZipCode = () => {

	if (!plpZipCodeDoms.length) return;

	Logger.info(`${NAME} > PLP Zip Code loaded`);

	plpZipCodeDoms.forEach((plpZipCodeDom) => {

		const plpZipCodeAction = plpZipCodeDom.querySelector('[data-plp-zip-code-action]');
		const plpZipCodeInput = plpZipCodeDom.querySelector('[data-plp-zip-code-input]');

		if (!plpZipCodeAction || !plpZipCodeInput) return;

		// Check the localStorage and set the value of the input
		const existingZipCode = localStorage.getItem('zipCode');
		if (existingZipCode) {
			plpZipCodeInput.value = existingZipCode;

			// Set the PLP Zip Code facet filter
			setPLPZipCodeFacetFilter(existingZipCode);
		}



		// The plpZipCodeDom is a form element. We need to prevent the form submission
		// and instead, set the zip code in the localStorage and set the PLP Zip Code facet filter
		plpZipCodeDom.addEventListener('submit', (e) => {
			e.preventDefault();

			const zipCodeValue = plpZipCodeInput.value;

			if (!zipCodeValue) return;

			// Set the zip code in the localStorage
			localStorage.setItem('zipCode', zipCodeValue);

			// Set the PLP Zip Code facet filter
			setPLPZipCodeFacetFilter(zipCodeValue);
		});
	});
}

/**
 * Set PLP Zip Code facet filter
 *
 *  * If a local zip code is selected we show:
		- NondirectShip
		- DirectShip
		- Direct Floral
		- Exclusive

	If a non-local (domestic) zip code is selected we show:
		- NondirectShip
		- DirectShip
		- Direct Floral

	If a non-local (international) zip code is selected we show:
		- NondirectShip
		- DirectShip
 */
const setPLPZipCodeFacetFilter = (zipCodeValue) => {
	Logger.info(`${NAME} > setPLPZipCodeFacetFilter`);

	if (!zipCodeValue) return;
	if (!window.localZipCodes) return;

	// Get the filters dom
	const filtersDom = document.querySelector('.facets__filters');
	if (!filtersDom) return;

	// Get all the checkbox doms
	let directFloralCheckboxDom = document.querySelector(`input[name="filter.p.m.custom.shipping_type"][value="Direct Floral"]`);
	let directShipCheckboxDom = document.querySelector(`input[name="filter.p.m.custom.shipping_type"][value="Direct Ship"]`);
	let nonDirectShipCheckboxDom = document.querySelector(`input[name="filter.p.m.custom.shipping_type"][value="Non-Direct Ship"]`);
	let exclusiveCheckboxDom = document.querySelector(`input[name="filter.p.m.custom.shipping_type"][value="Exclusive"]`);

	// Record the values of all the checkboxes, also checking if each exists first
	const directFloralCheckboxValue = directFloralCheckboxDom ? directFloralCheckboxDom.checked : null;
	const directShipCheckboxValue = directShipCheckboxDom ? directShipCheckboxDom.checked : null;
	const nonDirectShipCheckboxValue = nonDirectShipCheckboxDom ? nonDirectShipCheckboxDom.checked : null;
	const exclusiveCheckboxValue = exclusiveCheckboxDom ? exclusiveCheckboxDom.checked : null;

	// Check if the zip code is domestic
	const isDomestic = KrempRules.zipCodeIsDomestic(zipCodeValue);
	Logger.info(`${NAME} > setPLPZipCodeFacetFilter > isDomestic: ${isDomestic}`);

	// Determine the type of zip code

	// Check if the zip code is in the local list
	if (window.localZipCodes.includes(zipCodeValue)) {
		Logger.info(`${NAME} > Zip code is in the local list`);
		if (directFloralCheckboxDom) directFloralCheckboxDom.checked = true;
		if (directShipCheckboxDom) directShipCheckboxDom.checked = true;
		if (nonDirectShipCheckboxDom) nonDirectShipCheckboxDom.checked = true;
		if (exclusiveCheckboxDom) exclusiveCheckboxDom.checked = true;

		// Check if the zip code is domestic
	} else if (isDomestic) {
		Logger.info(`${NAME} > Zip code appears to be domestic, but not in the local list`);
		if (directFloralCheckboxDom) directFloralCheckboxDom.checked = true;
		if (directShipCheckboxDom) directShipCheckboxDom.checked = true;
		if (nonDirectShipCheckboxDom) nonDirectShipCheckboxDom.checked = true;
		if (exclusiveCheckboxDom) exclusiveCheckboxDom.checked = false;

		// Check if the zip code is international
	} else {
		Logger.info(`${NAME} > Zip code appears to be international`);
		if (directFloralCheckboxDom) directFloralCheckboxDom.checked = false;
		if (directShipCheckboxDom) directShipCheckboxDom.checked = true;
		if (nonDirectShipCheckboxDom) nonDirectShipCheckboxDom.checked = true;
		if (exclusiveCheckboxDom) exclusiveCheckboxDom.checked = false;
	}

	// Compare the new values of the checkboxes with the recorded values, checking to see if the checkboxes exists first
	let directShipCheckboxValueChanged = false;
	let directFloralCheckboxValueChanged = false;
	let exclusiveCheckboxValueChanged = false;
	let nonDirectShipCheckboxValueChanged = false;

	if (directFloralCheckboxDom) {
		directFloralCheckboxValueChanged = directFloralCheckboxValue !== directFloralCheckboxDom.checked;
		// console.log('directFloralCheckboxDom', directShipCheckboxValue, directFloralCheckboxDom.checked);
	}
	if (directShipCheckboxDom) {
		directShipCheckboxValueChanged = directShipCheckboxValue !== directShipCheckboxDom.checked;
		// console.log('directShipCheckboxDom', directShipCheckboxValue, directShipCheckboxDom.checked);
	}
	if (nonDirectShipCheckboxDom) {
		nonDirectShipCheckboxValueChanged = nonDirectShipCheckboxValue !== nonDirectShipCheckboxDom.checked;
		// console.log('nonDirectShipCheckboxDom', nonDirectShipCheckboxValue, nonDirectShipCheckboxDom.checked);
	}
	if (exclusiveCheckboxDom) {
		exclusiveCheckboxValueChanged = exclusiveCheckboxValue !== exclusiveCheckboxDom.checked;
		// console.log('exclusiveCheckboxDom', exclusiveCheckboxValue, exclusiveCheckboxDom.checked);
	}

	// Dispatch the input event to update the filters, only if the values have changed
	if (directFloralCheckboxValueChanged || directShipCheckboxValueChanged || nonDirectShipCheckboxValueChanged || exclusiveCheckboxValueChanged) {
		filtersDom.dispatchEvent(new Event('input'));
	}
}

// Bootstrap global functionality
Global();

// Bootstrap page-specific functionality
init();

// Do not edit below this line...
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
Logger.log(`${NAME} > loaded`);
